import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { MappingType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useModalControl, useAlert, useDebouncedValue } from '@frontend/design-system';
import { AppointmentTypeBulkColorAssignmentBanner } from '../../components';
import { AddEditAppointmentTypeModal } from '../../components/AppointmentTypesV3/add-edit-appointment-type-modal';
import { GlobalAppointmentTypesPaymentForm } from '../../components/AppointmentTypesV3/appointment-type-payment-fields';
import { AppointmentTypesTable } from '../../components/AppointmentTypesV3/AppointmentTypesTable';
import {
  useAppointmentTypesCustomizationAndFeatureFlagsHelper,
  useCheckPaymentsEnabled,
  useSchedulingLocationInfo,
} from '../../hooks';
import { useAppointmentTypesHelperShallowStore } from '../../stores/use-appointment-types-helper-store';
import { PageConfig } from '../../types';

type AppointmentTypeV3PageProps = {
  locationId?: string;
};

export const AppointmentTypeV3Page = ({ locationId }: AppointmentTypeV3PageProps) => {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { selectedLocationIds, isMultiLocation } = useSchedulingLocationInfo();
  const selectedLocationId = locationId || selectedLocationIds[0];

  const { setPaymentsEnabled, setIsOnlineSchedulingEnabled } = useAppointmentTypesHelperShallowStore(
    'setPaymentsEnabled',
    'setIsOnlineSchedulingEnabled'
  );

  const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | null>(null);

  const { modalProps: editModalProps, triggerProps: editModalTriggerProps } = useModalControl();

  const [pageConfig, setPageConfig] = useState<PageConfig>({
    page: 1,
    limit: 50,
    searchValue: '',
  });

  const debouncedSearchValue = useDebouncedValue(pageConfig.searchValue, 400);

  const { mutateAsync: archiveAppointmentType } = SchedulerV3.Mutations.useUpdateAppointmentTypeMutation({
    optimisticUpdate: true,
  });

  const { data: appointmentTypeData, isLoading: isLoadingAppointmentTypesData } =
    SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
      debouncedSearchValue,
      pageConfig,
      selectedLocationId,
      selectedLocationIds: [selectedLocationId],
      childLocationId: isMultiLocation ? selectedLocationId : '',
    });

  const listMappingsQuery = SchedulerV3.Queries.useListMappingsQuery({
    request: {
      locationIds: [selectedLocationId],
      mappingType: MappingType.PROVIDER_APPOINTMENT_TYPE,
      showInactive: true,
    },
    options: {
      enabled: !!selectedLocationId,
    },
  });

  const { isLoading: isLoadingCFInfo, isOnlineSchedulingEnabled } =
    useAppointmentTypesCustomizationAndFeatureFlagsHelper(selectedLocationId);

  const { isPaymentsEnabled } = useCheckPaymentsEnabled(selectedLocationId);

  useEffect(() => {
    setIsOnlineSchedulingEnabled(isOnlineSchedulingEnabled);
    setPaymentsEnabled(isPaymentsEnabled);
  }, [isPaymentsEnabled, isOnlineSchedulingEnabled]);

  const handleOnEdit = (appointmentType: AppointmentType) => {
    setSelectedAppointmentType(appointmentType);
    editModalTriggerProps.onClick();
  };

  const handleOnArchive = (appointmentType: AppointmentType) => {
    setSelectedAppointmentType(appointmentType);
    archiveAppointmentType({
      appointmentTypeId: appointmentType.id,
      childLocationId: isMultiLocation ? appointmentType.locationId : undefined,
      hidden: true,
      details: {
        ...appointmentType.details,
        act: !appointmentType.details?.act,
        sobs: false,
      },
    })
      .then((res) => {
        if (res) {
          alert.success(
            t('Appointment type {{status}} successfully', {
              status: !!appointmentType.details?.act ? 'archived' : 'unarchived',
            })
          );
        }
      })
      .catch(() => {
        alert.error(
          t('Failed to {{status}} appointment type', {
            status: !!appointmentType.details?.act ? 'archive' : 'unarchive',
          })
        );
      });
  };

  const editModalOnClose = () => {
    setSelectedAppointmentType(null);
    editModalProps.onClose();
  };

  const isLoading = isLoadingAppointmentTypesData || isLoadingCFInfo || listMappingsQuery.isFetching;
  return (
    <section css={containerStyle}>
      <AppointmentTypeBulkColorAssignmentBanner locationIds={[selectedLocationId]} />
      <div css={{ margin: theme.spacing(3) }}>
        {isPaymentsEnabled && isOnlineSchedulingEnabled && (
          <GlobalAppointmentTypesPaymentForm locationId={selectedLocationId} />
        )}
        <AppointmentTypesTable
          locationId={selectedLocationId}
          appointmentTypesData={appointmentTypeData?.appointmentTypes ?? []}
          mappingList={listMappingsQuery.data?.mappings || []}
          totalCount={appointmentTypeData?.totalCount || 0}
          isLoading={isLoading}
          pageConfig={pageConfig}
          setPageConfig={setPageConfig}
          onEdit={handleOnEdit}
          onArchive={handleOnArchive}
          isPaymentsEnabled={isPaymentsEnabled}
          debouncedSearchValue={debouncedSearchValue}
          isMultiLocation={isMultiLocation}
        />
        {selectedAppointmentType && (
          <AddEditAppointmentTypeModal
            data={selectedAppointmentType}
            locationId={selectedLocationId}
            {...editModalProps}
            onClose={editModalOnClose}
          />
        )}
      </div>
    </section>
  );
};

const containerStyle = css({
  position: 'relative',
  background: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
  boxShadow: theme.shadows.light,
  overflow: 'auto',
});
